/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/MohSourcesActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Actions column renderer for MOH Sources DataGrid.
    Shows edit, duplicate, delete, and view XML actions.
────────────────────────────────────────────────────────────────*/

import { Edit, Trash2, Copy, Code } from 'lucide-react';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { MOHSource } from '../types';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';

interface MohSourcesActionsProps {
  item: MOHSource;
  onEdit: (moh: MOHSource) => void;
  onDuplicate: (moh: MOHSource) => void;
  onDelete: (moh: MOHSource) => void;
  onViewXml: (moh: MOHSource) => void;
}

/**
 * Render actions column for MOH Source item
 */
export function MohSourcesActions({
  item,
  onEdit,
  onDuplicate,
  onDelete,
  onViewXml,
}: MohSourcesActionsProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common:actions.edit') || t('common:edit') || 'Edit'} position="top">
        <button
          onClick={() => onEdit(item)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-md transition-colors"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip
        content={t('common:actions.duplicate') || t('common:duplicate') || 'Duplicate'}
        position="top"
      >
        <button
          onClick={() => onDuplicate(item)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-md transition-colors"
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      {isDebugModeActive && (
        <SimpleTooltip content={t('extensions.actions.viewXml') || 'View XML'} position="top">
          <button
            onClick={() => onViewXml(item)}
            className="p-2 text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/20 rounded-md transition-colors"
          >
            <Code className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      )}

      {!item.is_default && (
        <SimpleTooltip
          content={t('common:actions.delete') || t('common:delete') || 'Delete'}
          position="top"
        >
          <button
            onClick={() => onDelete(item)}
            className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-md transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </SimpleTooltip>
      )}
    </div>
  );
}

